import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Payment Condition Cell Renderer Component
 *
 * Renders payment condition information with code (bold) and name.
 *
 * Features:
 * - Displays external code in bold on first line
 * - Displays name below code
 * - Falls back to "-" for null/undefined values
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'paymentConditionShort',
 *   headerName: 'Condición de Pago',
 *   cellRenderer: PaymentConditionCellRendererComponent,
 *   sortable: true
 * }
 * ```
 *
 * Output examples:
 * - "30D
 *    30 Días"
 * - "CONT
 *    Contado"
 */
@Component({
  selector: 'app-payment-condition-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="text-content flex flex-col">
      <div *ngIf="code" class="f-10">
        <b>{{ code }}</b>
      </div>
      <span>{{ name }}</span>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    .f-10 {
      font-size: 10px;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentConditionCellRendererComponent implements CellRenderer<any, any> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Get payment condition code from externalKeys
   */
  get code(): string | null {
    const pc = this.params?.value;
    if (!pc || !pc.externalKeys || !pc.externalKeys.code) {
      return null;
    }
    return pc.externalKeys.code;
  }

  /**
   * Get payment condition name
   */
  get name(): string {
    const pc = this.params?.value;
    return pc?.name || '-';
  }
}
